/*!
 * liveOutbox.js — what the console sends the projector, a task's worth at a time
 * © 2026 Rob Canning — GPLv3
 *
 * Run-all, a cluster, a line with its flash and its resets: a message each
 * was 60 and more in one go, and a relay passes a client 60 a second
 * (server/liveRelay.js) and drops the rest without a word. What one task
 * queues goes as one message, or a few when one would pass the relay's
 * 64 KB.
 */

/** Bytes of JSON a message carries at most: under the relay's 64 KB, with room for its envelope. */
export const MAX_MESSAGE_BYTES = 48 * 1024;

const encoder = new TextEncoder();
const bytesOf = item => encoder.encode(JSON.stringify(item)).length;

/** items in order, in runs whose JSON stays under maxBytes (one item over it goes alone). */
export function chunkBySize(items, maxBytes = MAX_MESSAGE_BYTES) {
  const out = [];
  let run = [], size = 2;
  for (const item of items) {
    const n = bytesOf(item) + 1;
    if (run.length && size + n > maxBytes) { out.push(run); run = []; size = 2; }
    run.push(item);
    size += n;
  }
  if (run.length) out.push(run);
  return out;
}

/**
 * A queue that hands what it holds to `flush` once the current task is done.
 * @param {(items: any[]) => void} flush
 * @returns {(item: any) => void} queue one item
 */
export function batched(flush) {
  let items = [], queued = false;
  return item => {
    items.push(item);
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      const out = items;
      items = [];
      queued = false;
      flush(out);
    });
  };
}
